import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { track } from "../../lib/tracking";
import { Button } from "../../components/ui/button";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from "../../components/ui/alert-dialog";
import { toast } from "sonner";
import { Flag, Eraser, ChevronRight, ChevronLeft, Grid3x3, X } from "lucide-react";

const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

export default function TestEngine() {
  const { testId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [marked, setMarked] = useState([]);
  const [visited, setVisited] = useState([]);
  const [remaining, setRemaining] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const attemptId = useRef(null);
  const submittedRef = useRef(false);

  useEffect(() => {
    api.post(`/tests/${testId}/start`).then((r) => {
      const d = r.data;
      attemptId.current = d.attempt_id;
      setData(d); setAnswers(d.answers || {}); setMarked(d.marked || []);
      setRemaining(d.remaining_seconds);
      track("StartTest", { test_id: testId });
    }).catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); nav(-1); });
  }, [testId]);

  // timer
  useEffect(() => {
    if (!data) return;
    const t = setInterval(() => setRemaining((r) => {
      if (r <= 1) { clearInterval(t); doSubmit(true); return 0; }
      return r - 1;
    }), 1000);
    return () => clearInterval(t);
  }, [data]);

  const save = useCallback(async (a = answers, m = marked, v = visited) => {
    if (!attemptId.current || submittedRef.current) return;
    try { await api.post("/attempts/save", { attempt_id: attemptId.current, answers: a, marked: m, visited: v }); } catch (e) {}
  }, [answers, marked, visited]);

  // autosave every 15s
  useEffect(() => { const t = setInterval(() => save(), 15000); return () => clearInterval(t); }, [save]);

  if (!data) return <div className="min-h-screen bg-ink flex items-center justify-center"><div className="w-8 h-8 rounded-full border-2 border-ember border-t-transparent animate-spin" /></div>;

  const questions = data.questions;
  const q = questions[idx];
  const qStatus = (i) => {
    const id = questions[i].id;
    const ans = answers[id] && (Array.isArray(answers[id]) ? answers[id].length : true);
    const mk = marked.includes(id);
    if (ans && mk) return "bg-status-ansreview text-white";
    if (mk) return "bg-status-review text-white";
    if (ans) return "bg-status-answered text-white";
    if (visited.includes(id) || i === idx) return "bg-status-notanswered text-white";
    return "bg-status-notvisited/60 text-white";
  };

  const markVisited = (id) => setVisited((v) => v.includes(id) ? v : [...v, id]);

  const selectOption = (optId) => {
    const next = { ...answers, [q.id]: q.multiple_correct
      ? (answers[q.id]?.includes(optId) ? answers[q.id].filter((x) => x !== optId) : [...(answers[q.id] || []), optId])
      : [optId] };
    setAnswers(next); save(next, marked, visited);
  };
  const clearResp = () => { const n = { ...answers }; delete n[q.id]; setAnswers(n); save(n); };
  const goto = (i) => { markVisited(q.id); setIdx(i); markVisited(questions[i].id); setPaletteOpen(false); };
  const saveNext = () => { markVisited(q.id); if (idx < questions.length - 1) goto(idx + 1); };
  const markReview = () => {
    const m = marked.includes(q.id) ? marked : [...marked, q.id];
    setMarked(m); save(answers, m, visited); saveNext();
  };

  const doSubmit = async (auto) => {
    if (submittedRef.current) return;
    submittedRef.current = true; setSubmitting(true);
    try {
      const { data: res } = await api.post("/attempts/submit", { attempt_id: attemptId.current, answers });
      track("CompleteTest", { test_id: testId, score: res.result.score });
      toast.success(auto ? "Time up — test submitted." : "Test submitted!");
      nav(`/result/${res.attempt_id}`, { replace: true });
    } catch (e) { toast.error("Submit failed. Retrying…"); submittedRef.current = false; setSubmitting(false); }
  };

  const answeredCount = Object.keys(answers).filter((k) => answers[k] && (Array.isArray(answers[k]) ? answers[k].length : true)).length;
  const selected = answers[q.id] || [];
  const low = remaining < 60;

  const Palette = () => (
    <div className="p-4">
      <div className="flex flex-wrap gap-1.5 text-[10px] mb-4">
        <Leg c="bg-status-answered" t="Answered" /><Leg c="bg-status-notanswered" t="Not Ans" />
        <Leg c="bg-status-review" t="Review" /><Leg c="bg-status-notvisited/60" t="Not Visited" />
      </div>
      <div className="grid grid-cols-6 sm:grid-cols-5 gap-2">
        {questions.map((qq, i) => (
          <button key={qq.id} onClick={() => goto(i)} data-testid={`question-palette-item-${i + 1}`}
            className={`aspect-square rounded-lg text-xs font-mono font-medium ${qStatus(i)} ${i === idx ? "ring-2 ring-ember" : ""}`}>{i + 1}</button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-ink flex flex-col">
      {/* Header */}
      <header className="glass-nav border-b border-line px-4 h-14 flex items-center justify-between shrink-0">
        <div className="text-sm font-medium truncate max-w-[45%]">{data.test.name}</div>
        <div className="flex items-center gap-3">
          <div className={`font-mono text-sm px-3 py-1 rounded-lg ${low ? "bg-red-500/20 text-red-400 animate-pulse" : "bg-card text-amber-400"}`} data-testid="test-timer-display">{fmt(remaining)}</div>
          <Button size="sm" className="btn-ember rounded-full" onClick={() => setConfirmOpen(true)} data-testid="test-btn-submit-exam">Submit</Button>
          <button className="lg:hidden" onClick={() => setPaletteOpen(true)} data-testid="test-palette-toggle"><Grid3x3 className="w-5 h-5" /></button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Question area */}
        <div className="flex-1 flex flex-col overflow-y-auto">
          <div className="container-px py-6 max-w-3xl w-full mx-auto flex-1">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-content-secondary" data-testid="question-number-display">Question {idx + 1} of {questions.length}</span>
              <span className="text-xs bg-ember-soft text-ember px-2 py-0.5 rounded-full">{q.subject} • {q.marks || 1} mark{(q.marks || 1) > 1 ? "s" : ""}</span>
            </div>
            <p className="text-base leading-relaxed mb-6">{q.question_text}</p>
            {q.question_image && <img src={q.question_image} alt="" className="rounded-lg mb-6 max-h-64" />}
            <div className="space-y-3">
              {q.options.map((o) => {
                const sel = selected.includes(o.id);
                return (
                  <button key={o.id} onClick={() => selectOption(o.id)} data-testid={`test-option-radio-${o.id}`}
                    className={`w-full text-left px-4 py-3 rounded-xl border transition-all flex items-center gap-3 ${sel ? "border-ember bg-ember-soft" : "border-line hover:border-content-muted"}`}>
                    <span className={`w-6 h-6 rounded-full border flex items-center justify-center text-xs font-mono shrink-0 ${sel ? "border-ember bg-ember text-white" : "border-content-muted"}`}>{o.id.toUpperCase()}</span>
                    <span className="text-sm">{o.text}</span>
                  </button>
                );
              })}
            </div>
          </div>
          {/* Action bar */}
          <div className="sticky bottom-0 glass-nav border-t border-line px-4 py-3">
            <div className="max-w-3xl mx-auto flex flex-wrap items-center gap-2 justify-between">
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="border-line rounded-full" onClick={markReview} data-testid="test-btn-mark-review"><Flag className="w-3.5 h-3.5 mr-1" /> Mark & Next</Button>
                <Button size="sm" variant="outline" className="border-line rounded-full" onClick={clearResp} data-testid="test-btn-clear-response"><Eraser className="w-3.5 h-3.5 mr-1" /> Clear</Button>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" disabled={idx === 0} onClick={() => goto(idx - 1)}><ChevronLeft className="w-4 h-4" /></Button>
                <Button size="sm" className="btn-ember rounded-full px-5" onClick={saveNext} data-testid="test-btn-save-next">Save & Next <ChevronRight className="w-4 h-4 ml-1" /></Button>
              </div>
            </div>
          </div>
        </div>
        {/* Palette desktop */}
        <aside className="hidden lg:block w-72 border-l border-line bg-surface overflow-y-auto shrink-0"><Palette /></aside>
      </div>

      {/* Palette mobile drawer */}
      {paletteOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex justify-end">
          <div className="flex-1 bg-black/60" onClick={() => setPaletteOpen(false)} />
          <div className="w-72 bg-surface border-l border-line overflow-y-auto">
            <div className="flex justify-between items-center px-4 py-3 border-b border-line"><span className="text-sm font-medium">Questions</span><button onClick={() => setPaletteOpen(false)}><X className="w-5 h-5" /></button></div>
            <Palette />
          </div>
        </div>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="bg-card border-line">
          <AlertDialogHeader>
            <AlertDialogTitle>Submit test?</AlertDialogTitle>
            <AlertDialogDescription>You've answered {answeredCount} of {questions.length} questions. You can't change answers after submitting.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-line">Keep going</AlertDialogCancel>
            <AlertDialogAction className="btn-ember" disabled={submitting} onClick={() => doSubmit(false)} data-testid="confirm-submit">Submit now</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
const Leg = ({ c, t }) => <span className="flex items-center gap-1"><span className={`w-3 h-3 rounded ${c}`} /> {t}</span>;
